import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, Calendar, Mail, MessageSquare, Phone, User as UserIcon, ChevronDown, FileText, Save, Tag, Plus } from "lucide-react";
import { toast } from "sonner";
import { exportPatientHistoryPdf } from "@/utils/pdf";

const SERVICE_LABEL = {
  terapia_fisica: "Terapia Física",
  rehabilitacion: "Rehabilitación",
  quiropractica: "Quiropráctica",
  terapias_alternativas: "T. Alternativas",
  estetica: "Estética",
  consulta_general: "Consulta",
};

const SERVICE_DOT = {
  terapia_fisica: "bg-green-500",
  rehabilitacion: "bg-green-500",
  quiropractica: "bg-emerald-500",
  terapias_alternativas: "bg-amber-500",
  estetica: "bg-rose-500",
  consulta_general: "bg-slate-500",
};

const STATUS_BADGE = {
  pending: { label: "Pendiente", cls: "bg-slate-100 text-slate-700" },
  confirmed: { label: "Confirmada", cls: "bg-green-100 text-green-800" },
  cancelled: { label: "Cancelada", cls: "bg-red-100 text-red-700" },
};

const Stat = ({ value, label, accent = "green" }) => (
  <div className={`rounded-xl border bg-white px-3 py-2.5 text-center border-${accent}-100`}>
    <div className="text-xl font-bold text-slate-900">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
  </div>
);

export const PatientHistoryDrawer = ({ open, onClose, token, API, phone, email, name }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState("appointments");
  const [notesDraft, setNotesDraft] = useState("");
  const [tagsDraft, setTagsDraft] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (!open || (!phone && !email)) return;
    setLoading(true);
    setData(null);
    const params = new URLSearchParams();
    if (phone) params.set("phone", phone);
    if (email) params.set("email", email);
    axios
      .get(`${API}/admin/patient?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setData(r.data);
        setNotesDraft(r.data?.profile?.notes || "");
        setTagsDraft(r.data?.profile?.tags || []);
      })
      .catch(() => setData({ appointments: [], contacts: [], inbound_emails: [], summary: {}, profile: {} }))
      .finally(() => setLoading(false));
  }, [open, phone, email, token, API]);

  const saveProfile = async (nextNotes, nextTags) => {
    setSavingProfile(true);
    try {
      const r = await axios.put(
        `${API}/admin/patient/profile`,
        { phone, email, notes: nextNotes ?? notesDraft, tags: nextTags ?? tagsDraft },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotesDraft(r.data.notes || "");
      setTagsDraft(r.data.tags || []);
      toast.success("Perfil actualizado");
    } catch {
      toast.error("Error al guardar perfil");
    } finally {
      setSavingProfile(false);
    }
  };

  const addTag = () => {
    const t = newTag.trim();
    if (!t) return;
    if (tagsDraft.find((x) => x.toLowerCase() === t.toLowerCase())) {
      setNewTag("");
      return;
    }
    const updated = [...tagsDraft, t];
    setNewTag("");
    saveProfile(undefined, updated);
  };

  const removeTag = (t) => {
    const updated = tagsDraft.filter((x) => x !== t);
    saveProfile(undefined, updated);
  };

  if (!open) return null;

  const summary = data?.summary || {};
  const appts = data?.appointments || [];
  const contacts = data?.contacts || [];
  const inbound = data?.inbound_emails || [];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex justify-end" data-testid="patient-drawer">
      <div className="bg-white w-full sm:w-[640px] h-full overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-green-100 text-green-700 grid place-items-center font-bold flex-shrink-0">
                {(name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <h2 className="font-display text-xl font-bold text-slate-900 truncate" data-testid="patient-drawer-name">{name || "Paciente"}</h2>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500 flex-wrap">
                  {phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{phone}</span>}
                  {email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{email}</span>}
                </div>
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 flex-shrink-0" data-testid="patient-drawer-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {loading && (
            <div className="grid place-items-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-green-600" />
            </div>
          )}

          {!loading && data && (
            <>
              {/* Action buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => exportPatientHistoryPdf({
                    name, phone, email,
                    summary, profile: { notes: notesDraft, tags: tagsDraft },
                    appointments: appts, contacts,
                  })}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-1.5 text-xs font-semibold hover:bg-slate-800"
                  data-testid="patient-export-pdf"
                >
                  <FileText className="h-3.5 w-3.5" /> Exportar PDF
                </button>
              </div>

              {/* Tags */}
              <div data-testid="patient-tags-section">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Etiquetas</span>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {tagsDraft.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-green-100 text-green-800 border border-green-200 px-3 py-1 text-xs font-medium" data-testid={`patient-tag-${t}`}>
                      {t}
                      <button type="button" onClick={() => removeTag(t)} className="hover:text-red-600" title="Quitar etiqueta">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <div className="inline-flex items-center gap-1">
                    <input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                      placeholder="VIP, Paquete..."
                      className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-green-400 w-32"
                      data-testid="patient-tag-input"
                    />
                    <button type="button" onClick={addTag} className="h-7 w-7 rounded-full bg-green-600 text-white grid place-items-center hover:bg-green-700" data-testid="patient-tag-add">
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Clinical notes */}
              <div data-testid="patient-notes-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 text-slate-500" />
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Notas clínicas</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => saveProfile(notesDraft, undefined)}
                    disabled={savingProfile || notesDraft === (data?.profile?.notes || "")}
                    className="inline-flex items-center gap-1 rounded-full bg-green-600 text-white px-3 py-1 text-[11px] font-semibold disabled:opacity-50 hover:bg-green-700"
                    data-testid="patient-notes-save"
                  >
                    {savingProfile ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Guardar
                  </button>
                </div>
                <textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  rows={4}
                  placeholder="Anotaciones del terapeuta: padecimiento principal, plan de tratamiento, alergias, preferencias..."
                  className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-green-400 resize-none"
                  data-testid="patient-notes-textarea"
                />
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="patient-stats">
                <Stat value={summary.total_appointments || 0} label="Citas total" />
                <Stat value={summary.future || 0} label="Futuras" accent="emerald" />
                <Stat value={summary.past || 0} label="Pasadas" />
                <Stat value={summary.messages || 0} label="Mensajes" />
              </div>

              {summary.most_used_service && (
                <div className="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-3 text-sm flex flex-wrap items-center gap-3">
                  <UserIcon className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-600">Servicio más solicitado:</span>
                  <span className="inline-flex items-center gap-1.5 font-semibold text-slate-800">
                    <span className={`h-2 w-2 rounded-full ${SERVICE_DOT[summary.most_used_service] || "bg-slate-400"}`} />
                    {SERVICE_LABEL[summary.most_used_service] || summary.most_used_service}
                  </span>
                  {summary.first_seen && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span className="text-slate-600">Primer registro: <strong className="text-slate-800">{String(summary.first_seen).slice(0, 10)}</strong></span>
                    </>
                  )}
                </div>
              )}

              {/* Section tabs */}
              <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                {[
                  { id: "appointments", label: `Citas (${appts.length})`, icon: Calendar },
                  { id: "contacts", label: `Mensajes (${contacts.length})`, icon: MessageSquare },
                  { id: "inbound", label: `Bandeja (${inbound.length})`, icon: Mail },
                ].map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSection(s.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                      section === s.id ? "bg-green-600 text-white" : "text-slate-700 hover:bg-slate-100"
                    }`}
                    data-testid={`patient-tab-${s.id}`}
                  >
                    <s.icon className="h-3 w-3" /> {s.label}
                  </button>
                ))}
              </div>

              {section === "appointments" && (
                <div className="space-y-2" data-testid="patient-appts">
                  {appts.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Sin citas registradas.</p>}
                  {appts.map((a) => {
                    const status = STATUS_BADGE[a.status] || STATUS_BADGE.pending;
                    const today = new Date().toISOString().slice(0, 10);
                    const isFuture = (a.date || "") >= today;
                    return (
                      <div key={a.id} className={`rounded-xl border p-3 ${isFuture ? "border-green-200 bg-green-50/40" : "border-slate-100 bg-white"}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 text-sm">
                            <span className={`h-2 w-2 rounded-full ${SERVICE_DOT[a.service] || "bg-slate-400"}`} />
                            <span className="font-semibold text-slate-900">{a.date}</span>
                            <span className="text-slate-500">{a.time}</span>
                            <span className="text-slate-300">·</span>
                            <span className="text-slate-700">{SERVICE_LABEL[a.service] || a.service}</span>
                          </div>
                          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${status.cls}`}>{status.label}</span>
                        </div>
                        {a.notes && <p className="mt-1.5 text-xs text-slate-600 italic">"{a.notes}"</p>}
                      </div>
                    );
                  })}
                </div>
              )}

              {section === "contacts" && (
                <div className="space-y-2" data-testid="patient-contacts">
                  {contacts.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Sin mensajes de contacto.</p>}
                  {contacts.map((c) => (
                    <div key={c.id} className="rounded-xl border border-slate-100 bg-white p-3">
                      <div className="text-xs text-slate-500">{String(c.created_at || "").slice(0, 16).replace("T", " ")}</div>
                      <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{c.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {section === "inbound" && (
                <div className="space-y-2" data-testid="patient-inbound">
                  {inbound.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Sin correos recibidos de este email.</p>}
                  {inbound.map((m) => (
                    <details key={m.id} className="rounded-xl border border-slate-100 bg-white p-3">
                      <summary className="flex items-center justify-between cursor-pointer">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{m.subject}</p>
                          <p className="text-xs text-slate-500">{m.date}</p>
                        </div>
                        <ChevronDown className="h-4 w-4 text-slate-400" />
                      </summary>
                      <pre className="mt-3 text-xs text-slate-700 whitespace-pre-wrap break-words bg-slate-50 rounded-lg p-3 max-h-[300px] overflow-auto font-mono">{m.body_preview}</pre>
                    </details>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientHistoryDrawer;
